import {
  Button,
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, Link as RouterLink } from 'react-router-dom';
import Iconify from '../components/Iconify';
import Label from '../components/Label';
import { LoadingBox } from '../components/LoadingBox';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { useHttpClient } from '../hooks/useHttpClient';
import { useTableUtils } from '../hooks/useTableUtils';
import { TableHeader, TableMoreMenu, TableToolbar } from '../sections/@dashboard/common';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'slug', label: 'Url Slug', alignRight: false },
  { id: 'description', label: 'Summary', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'created_at', label: 'Creation Date', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

export default function Career() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { http } = useHttpClient();
  const {
    page,
    handleChangeRowsPerPage,
    handleChangePage,
    order,
    selected,
    setSelected,
    handleSelected,
    handleSelectAllClick,
    orderBy,
    handleRequestSort,
    filterName,
    filteredUsers,
    handleFilterByName,
    handleFilterByStatus,
    rowsPerPage,
    emptyRows,
    isRecordNotFound,
  } = useTableUtils({ list });

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadList() {
    setLoading(true);
    await http.get('jobs').then(setList);
    setLoading(false);
  }

  async function handleDelete(id) {
    setLoading(true);
    await http.delete(`jobs/${id}`);
    await loadList();
  }

  async function handleArchiveClick() {
    setLoading(true);
    Promise.all(selected.map((id) => http.put(`jobs`, { id, status: 0 }))).then(() => {
      loadList();
      setSelected([]);
      setLoading(false);
    });
  }

  return (
    <Page title="Career List">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Career
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/careers/detail/new"
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            New Career
          </Button>
        </Stack>

        <Card>
          <TableToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onFilterStatus={handleFilterByStatus}
            onArchiveClick={() => handleArchiveClick()}
          />

          <Scrollbar>
            <LoadingBox loading={loading} />
            <TableContainer sx={{ minWidth: 800, minHeight: loading ? 350 : undefined }}>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={list.length}
                  filteredRowCount={filteredUsers.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, title, slug, description, status, created_at: createdAt } = row;
                    const isItemSelected = selected.indexOf(id) !== -1;

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={isItemSelected} onChange={(event) => handleSelected(event, id)} />
                        </TableCell>
                        <TableCell align="left">
                          <Link to={`/careers/detail/${id}`}>{title}</Link>
                        </TableCell>
                        <TableCell align="left">{slug}</TableCell>
                        <TableCell align="left">{description}</TableCell>
                        <TableCell align="left">
                          <Label variant="ghost" color={status === 1 ? 'success' : 'error'}>
                            {status === 1 ? 'Published' : 'Archived'}
                          </Label>
                        </TableCell>
                        <TableCell align="left">{new Date(createdAt).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell align="right">
                          <TableMoreMenu onDelete={() => handleDelete(id)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {!loading && isRecordNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={list.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
